// src/pages/SchemePayments.jsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Select from "react-select";
import toast from "react-hot-toast";
import { skipToken } from "@reduxjs/toolkit/query";
import InvoicePrintPreviewModal from "../components/InvoicePrintPreviewModal";
import {
  FaRedoAlt,
  FaUserCircle,
  FaMoneyBillWave,
  FaWallet,
} from "react-icons/fa";
import { FiPrinter, FiRotateCcw } from "react-icons/fi";

import { useGetAllCustomersQuery } from "../toolkitServices/customerApi";
import { useGetAllGoldSchemeEnrollmentsQuery } from "../toolkitServices/goldSchemeApi";
import {
  useGetCustomerTransactionsQuery,
  useReverseCustomerTransactionMutation,
} from "../toolkitServices/customerTransactionApi";

import SchemePayWithdrawModal from "../components/Scheme/SchemePayWithdrawModal";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

/* ---------------- SalesList theme tokens ---------------- */
const BRAND = {
  header: "#9481ce",
  primary: "#6b59c6",
  ring: "#7c3aed",
};

/* ---------------- animations ---------------- */
const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 14 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.45, ease: "easeOut" },
  },
});

const listVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.04, duration: 0.35, ease: "easeOut" },
  }),
};

/* ---------------- helpers ---------------- */
const toOpt = (c) => ({
  value: c?._id,
  label: `${c?.customerName || "Customer"}${
    c?.customerId ? ` · ${c.customerId}` : ""
  }`,
  raw: c,
});

const toEnrollOpt = (e) => ({
  value: e?._id,
  label: `${e?.schemeName || "Scheme"}${
    e?.enrollmentCode ? ` · ${e.enrollmentCode}` : ""
  }`,
  raw: e,
});

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmtINR = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "₹0.00";
  return `₹${n.toFixed(2)}`;
};

const fmtG = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0.000 g";
  return `${n.toFixed(3)} g`;
};

const toYmd = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d).slice(0, 10);
  return dt.toISOString().slice(0, 10);
};

// ✅ robust ledger id pick
const pickTxLedgerId = (r) =>
  r?.ledgerId ||
  r?.ledger?._id ||
  r?.transactionId ||
  r?.receiptId ||
  r?._id ||
  null;

// ✅ reversal detection
const isReversalRow = (r) => {
  const st = String(r?.sourceType || "").toUpperCase();
  return !!r?.isReversal || st === "REVERSAL";
};

// ✅ scheme-like detection (includes REVERSAL rows mapped to SCHEME types)
const isSchemeLikeRow = (r) => {
  const st = String(r?.sourceType || "").toUpperCase();
  const dst = String(r?.displaySourceType || "").toUpperCase();
  const ost = String(r?.originalSourceType || "").toUpperCase();

  return (
    st === "SCHEME" ||
    st === "SCHEME_PAYOUT" ||
    dst === "SCHEME" ||
    dst === "SCHEME_PAYOUT" ||
    ost === "SCHEME" ||
    ost === "SCHEME_PAYOUT"
  );
};

// ✅ scheme txn type (works for REVERSAL rows too)
const pickSchemeTxnType = (r) => {
  const st = String(r?.sourceType || "").toUpperCase();
  const dst = String(r?.displaySourceType || "").toUpperCase();
  const ost = String(r?.originalSourceType || "").toUpperCase();
  return st !== "REVERSAL" ? st : dst || ost || st;
};

const isSchemePayoutRow = (r) => pickSchemeTxnType(r) === "SCHEME_PAYOUT";

/* ---------------- react-select styling ---------------- */
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 34,
    height: 34,
    borderRadius: 10,
    borderColor: state.isFocused ? BRAND.ring : "#e5e7eb",
    boxShadow: state.isFocused ? `0 0 0 3px ${BRAND.ring}22` : "none",
    "&:hover": { borderColor: BRAND.ring },
    fontSize: "0.8rem",
    backgroundColor: "white",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  singleValue: (base) => ({
    ...base,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#111827",
    fontWeight: 600,
  }),
  placeholder: (base) => ({ ...base, color: "#9ca3af", fontSize: "0.8rem" }),
  input: (base) => ({ ...base, margin: 0, padding: 0, color: "#111827" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.8rem",
    backgroundColor: state.isFocused
      ? "#ede9fe"
      : state.isSelected
        ? "#ddd6fe"
        : "white",
    color: "#111827",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: 12,
    overflow: "hidden",
  }),
};

const menuPortalTarget = typeof document !== "undefined" ? document.body : null;

/* ---------------- small UI components ---------------- */
const ListLoadingPill = ({ show }) => {
  return (
    <AnimatePresence>
      {show ? (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 shadow-sm"
        >
          <span className="relative inline-flex h-4 w-4 items-center justify-center">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-slate-300/60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-slate-400" />
          </span>
          Loading transactions…
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

const SkeletonTxRow = () => (
  <div className="rounded-2xl border bg-white px-3 py-2.5 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="h-5 w-16 rounded-full bg-slate-200 animate-pulse" />
          <div className="h-4 w-44 rounded bg-slate-200 animate-pulse" />
        </div>

        <div className="mt-2 space-y-2">
          <div className="h-3 w-64 rounded bg-slate-200 animate-pulse" />
          <div className="h-3 w-40 rounded bg-slate-200 animate-pulse" />
        </div>
      </div>

      <div className="shrink-0 text-right space-y-2">
        <div className="h-5 w-24 rounded bg-slate-200 animate-pulse ml-auto" />
        <div className="h-3 w-14 rounded bg-slate-200 animate-pulse ml-auto" />
        <div className="h-7 w-16 rounded-full bg-slate-200 animate-pulse ml-auto" />
      </div>
    </div>
  </div>
);

const ListShimmerOverlay = ({ show }) => (
  <AnimatePresence>
    {show ? (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="pointer-events-none absolute inset-0"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-white/65 via-white/40 to-white/65" />
        <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-r from-transparent via-slate-100/80 to-transparent animate-[shimmer_1.25s_linear_infinite]" />
        <style>{`
          @keyframes shimmer { 
            0% { transform: translateX(-100%); } 
            100% { transform: translateX(100%); } 
          }
        `}</style>
      </motion.div>
    ) : null}
  </AnimatePresence>
);

export default function SchemePayments() {
  const [customerOpt, setCustomerOpt] = useState(null);
  const [enrollOpt, setEnrollOpt] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState("PAY"); // PAY | WITHDRAW
  const [receiptPreview, setReceiptPreview] = useState({
    open: false,
    html: "",
    loading: false,
  });
  const [reverseTx, reverseState] = useReverseCustomerTransactionMutation();
  const [reverseOpen, setReverseOpen] = useState(false);
  const [reverseTarget, setReverseTarget] = useState(null); // { ledgerId, title, amountLabel }

  // ✅ requirement: show reversal + reversed entries also
  const [showReversed, setShowReversed] = useState(true);

  const customerId = customerOpt?.value || null;

  // ✅ modal close => focus back to customer select
  const customerSelectRef = useRef(null);
  const wasModalOpenRef = useRef(false);

  useEffect(() => {
    if (wasModalOpenRef.current && !modalOpen) {
      requestAnimationFrame(() => {
        customerSelectRef.current?.focus?.();
      });
    }
    wasModalOpenRef.current = modalOpen;
  }, [modalOpen]);

  const { data: customerRes } = useGetAllCustomersQuery({});
  const customers = useMemo(
    () => customerRes?.data || customerRes?.rows || customerRes || [],
    [customerRes],
  );
  const customerOptions = useMemo(
    () => (Array.isArray(customers) ? customers.map(toOpt) : []),
    [customers],
  );

  const {
    data: enrRes,
    isFetching: enrLoading,
    refetch: refetchEnrollments,
  } = useGetAllGoldSchemeEnrollmentsQuery(
    customerId ? { customerId, limit: 200, page: 1 } : skipToken,
  );

  const enrollments = useMemo(() => {
    const enrollmentsRaw =
      enrRes?.data ||
      enrRes?.items ||
      enrRes?.rows ||
      enrRes?.data?.rows ||
      enrRes ||
      [];
    return Array.isArray(enrollmentsRaw) ? enrollmentsRaw : [];
  }, [enrRes]);

  const enrollmentOptions = useMemo(
    () => enrollments.map(toEnrollOpt),
    [enrollments],
  );

  // ✅ IMPORTANT FIX: always pick latest enrollment object from newest enrollments list
  const selectedEnrollment = useMemo(() => {
    const id = enrollOpt?.value;
    if (!id) return null;

    return (
      enrollments.find((e) => String(e?._id) === String(id)) ||
      enrollOpt?.raw ||
      null
    );
  }, [enrollOpt, enrollments]);

  // ✅ OPTIONAL: bind Select value to fresh option from enrollmentOptions
  const selectedEnrollOpt = useMemo(() => {
    const id = enrollOpt?.value;
    if (!id) return null;
    return (
      enrollmentOptions.find((o) => String(o.value) === String(id)) || null
    );
  }, [enrollOpt, enrollmentOptions]);

  const {
    data: txnRes,
    isFetching: txnLoading,
    refetch: refetchTxns,
  } = useGetCustomerTransactionsQuery(
    customerId ? { customerId, page: 1, limit: 200, type: "ALL" } : skipToken,
  );

  const txRows = useMemo(() => {
    const raw = txnRes?.rows || txnRes?.data?.rows || txnRes?.data || [];
    return Array.isArray(raw) ? raw : [];
  }, [txnRes]);

  // ✅ include SCHEME + SCHEME_PAYOUT + REVERSAL(display/original scheme)
  const schemeRows = useMemo(() => {
    const rows = txRows.filter(isSchemeLikeRow);

    if (showReversed) return rows;

    // hide both: original reversed + reversal rows
    return rows.filter((r) => !r?.isReversed && !isReversalRow(r));
  }, [txRows, showReversed]);

  const filteredTx = useMemo(() => {
    if (!selectedEnrollment?._id) return schemeRows;
    const id = String(selectedEnrollment._id);

    return schemeRows.filter((r) => {
      const eId = r?.enrollmentId || r?.schemeEnrollmentId || null;
      return eId ? String(eId) === id : false;
    });
  }, [schemeRows, selectedEnrollment]);

  const activeFilteredTx = useMemo(
    () =>
      filteredTx.filter(
        (r) => !isReversalRow(r) && !r?.isReversed && !r?.reversed,
      ),
    [filteredTx],
  );

  const schemeSummary = useMemo(() => {
    const st = String(selectedEnrollment?.schemeType || "").toUpperCase();
    const isGram = st === "GRAM";
    const perMonth = num(selectedEnrollment?.schemeAmountPerMonth);
    const months = num(selectedEnrollment?.installmentMonths);
    const target = perMonth * months;

    const totals = activeFilteredTx.reduce(
      (acc, r) => {
        const isPayout = isSchemePayoutRow(r);
        const amount = isPayout ? num(r?.debit) : num(r?.credit);
        const grams = num(r?.schemeGrams);

        if (isPayout) {
          acc.withdrawnAmount += amount;
          acc.withdrawnGrams += grams;
        } else {
          acc.receivedAmount += amount;
          acc.receivedGrams += grams;
        }
        return acc;
      },
      {
        receivedAmount: 0,
        withdrawnAmount: 0,
        receivedGrams: 0,
        withdrawnGrams: 0,
      },
    );

    const received = isGram ? totals.receivedGrams : totals.receivedAmount;
    const withdrawn = isGram ? totals.withdrawnGrams : totals.withdrawnAmount;
    const net = received - withdrawn;
    const pending = Math.max(target - net, 0);
    const format = isGram ? fmtG : fmtINR;

    return {
      target: format(target),
      received: format(received),
      withdrawn: format(withdrawn),
      net: format(net),
      pending: format(pending),
    };
  }, [activeFilteredTx, selectedEnrollment]);

  const refreshAll = async () => {
    try {
      await Promise.allSettled([refetchEnrollments(), refetchTxns()]);
      toast.success("Refreshed");
    } catch {}
  };

  const openPay = () => {
    if (!customerId) return toast.error("Select customer");
    if (!selectedEnrollment?._id) return toast.error("Select enrollment");
    setModalAction("PAY");
    setModalOpen(true);
  };

  const openWithdraw = () => {
    if (!customerId) return toast.error("Select customer");
    if (!selectedEnrollment?._id) return toast.error("Select enrollment");
    setModalAction("WITHDRAW");
    setModalOpen(true);
  };

  const enrollBadge = useMemo(() => {
    if (!selectedEnrollment) return null;

    const st = String(selectedEnrollment?.schemeType || "").toUpperCase();
    const isGram = st === "GRAM";
    const perMonth = Number(selectedEnrollment?.schemeAmountPerMonth || 0);
    const paidInst = Number(selectedEnrollment?.totalPaidInstallments || 0);
    const instMonths = Number(selectedEnrollment?.installmentMonths || 0);

    return (
      <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[12px] font-extrabold text-slate-800 truncate">
              {selectedEnrollment?.schemeName || "Scheme"}{" "}
              <span className="text-slate-500 font-semibold">
                · {selectedEnrollment?.enrollmentCode || ""}
              </span>
            </div>

            <div className="mt-0.5 truncate text-[10px] text-slate-600">
              <span className="font-semibold">
                {isGram
                  ? `GRAM (${selectedEnrollment?.metalType || "GOLD"})`
                  : "AMOUNT"}
              </span>
              {" · "}
              Per Month:{" "}
              <span className="font-semibold">
                {isGram ? fmtG(perMonth) : fmtINR(perMonth)}
              </span>
              {" · "}
              Paid:{" "}
              <span className="font-semibold">
                {paidInst}/{instMonths}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center rounded-lg px-2 py-1 text-[10px] font-extrabold border ${
                isGram
                  ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                  : "bg-emerald-50 text-emerald-700 border-emerald-200"
              }`}
            >
              {isGram ? "GRAM" : "AMOUNT"}
            </span>

            <span className="inline-flex items-center rounded-lg border bg-white px-2 py-1 text-[10px] font-semibold text-slate-600">
              Status: {selectedEnrollment?.status || "-"}
            </span>
          </div>
        </div>
      </div>
    );
  }, [selectedEnrollment]);

  const showListSkeleton =
    !!customerId && txnLoading && filteredTx.length === 0;

  // ✅ reversible logic: can't reverse reversal rows, can't reverse already reversed
  const isReversibleRow = (r) => {
    if (!r) return false;

    if (isReversalRow(r)) return false;

    if (
      r?.isReversed ||
      r?.reversed ||
      String(r?.status || "").toUpperCase() === "REVERSED"
    )
      return false;

    return !!pickTxLedgerId(r);
  };

  const openReverse = (r, displayTitle, amountLabel) => {
    const ledgerId = pickTxLedgerId(r);
    if (!ledgerId) return toast.error("LedgerId not found.");
    setReverseTarget({ ledgerId, title: displayTitle, amountLabel });
    setReverseOpen(true);
  };

  const handleReceiptPrint = async (ledgerId) => {
    if (!ledgerId) return toast.error("LedgerId not found");

    try {
      setReceiptPreview({ open: true, html: "", loading: true });

      const res = await fetch(
        `${API_BASE_URL}/customers/transactions/${ledgerId}/receipt-print`,
        {
          method: "GET",
          credentials: "include",
        },
      );

      const html = await res.text();

      if (!res.ok) throw new Error(html || "Failed to load receipt");

      setReceiptPreview({ open: true, html, loading: false });
    } catch (err) {
      console.error(err);
      toast.error("Failed to load receipt preview");
      setReceiptPreview({ open: false, html: "", loading: false });
    }
  };

  const closeReceiptPreview = () => {
    setReceiptPreview({ open: false, html: "", loading: false });
  };
  const confirmReverse = async () => {
    try {
      const ledgerId = reverseTarget?.ledgerId;
      if (!ledgerId) return toast.error("LedgerId missing");

      await reverseTx({
        ledgerId,
        reason: "USER_REVERSE",
        note: "Reversed from SchemePayments UI",
      }).unwrap();

      toast.success("Transaction reversed");
      setReverseOpen(false);
      setReverseTarget(null);

      refetchTxns();
      refetchEnrollments();
    } catch (e) {
      const msg =
        e?.data?.message || e?.data?.msg || e?.error || "Reverse failed";
      toast.error(msg);
    }
  };

  return (
    <div className="w-full pb-3 md:h-[calc(100dvh-112px)] md:min-h-0 md:pb-0">
      <div className="grid grid-cols-1 items-stretch gap-3 md:h-full md:min-h-0 lg:grid-cols-[400px_minmax(0,1fr)] lg:gap-4">
        {/* Left side */}
        <motion.div
          variants={fadeUp(0)}
          initial="hidden"
          animate="visible"
          className="overflow-visible rounded-xl border bg-white shadow-sm md:flex md:h-full md:min-h-0 md:flex-col md:overflow-hidden"
        >
          <div className="flex items-center justify-between bg-[#9481ce] px-3 py-2.5 text-white sm:px-4 sm:py-3">
            <div className="flex items-center gap-2 min-w-0">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-white/10 border border-white/15">
                <FaMoneyBillWave className="text-white" />
              </span>
              <div className="min-w-0">
                <h1 className="text-sm font-semibold tracking-tight sm:text-base">
                  Scheme Payments
                </h1>
                <p className="text-[11px] text-violet-100/90 truncate">
                  Receive & Withdraw
                </p>
              </div>
            </div>

            <button
              onClick={refreshAll}
              className="group inline-flex items-center gap-2 rounded-full bg-white text-[#6b4fd8] px-3.5 py-2 text-[11px] font-semibold shadow-sm hover:bg-violet-50 transition"
              title="Refresh"
            >
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#6b59c6]/10">
                <FaRedoAlt className="text-[12px] text-[#6b59c6]" />
              </span>
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>

          <div className="bg-white p-3 sm:p-4 md:min-h-0 md:flex-1 md:overflow-auto">
            <div className="grid grid-cols-1 gap-2.5 sm:gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                  Customer <span className="text-rose-500">*</span>
                </label>
                <Select
                  ref={customerSelectRef}
                  value={customerOpt}
                  onChange={(v) => {
                    setCustomerOpt(v);
                    setEnrollOpt(null);
                  }}
                  options={customerOptions}
                  styles={selectStyles}
                  menuPortalTarget={menuPortalTarget}
                  placeholder="Select customer"
                  isClearable
                  menuPlacement="auto"
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                  Enrollment <span className="text-rose-500">*</span>
                </label>

                {/* ✅ UPDATED: value uses fresh option so badge updates properly */}
                <Select
                  value={selectedEnrollOpt}
                  onChange={(v) => setEnrollOpt(v)}
                  options={enrollmentOptions}
                  styles={selectStyles}
                  menuPortalTarget={menuPortalTarget}
                  placeholder={
                    customerId ? "Select enrollment" : "Select customer first"
                  }
                  isClearable
                  isLoading={enrLoading}
                  isDisabled={!customerId}
                  menuPlacement="auto"
                />

                {customerId && !enrLoading && !enrollments.length ? (
                  <p className="text-[11px] text-slate-500 mt-1">
                    No enrollments found.
                  </p>
                ) : null}
              </div>

              <div className="flex items-end justify-start gap-2">
                <motion.button
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98, y: 0 }}
                  type="button"
                  onClick={openPay}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl sm:rounded-full bg-[#6b59c6] px-4 py-2.5 text-[11px] font-semibold text-white shadow-sm hover:opacity-95 transition-all"
                >
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/15">
                    <FaMoneyBillWave className="text-[11px]" />
                  </span>
                  Receive
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98, y: 0 }}
                  type="button"
                  onClick={openWithdraw}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl sm:rounded-full bg-white text-[#6b59c6] px-4 py-2.5 text-[11px] font-semibold border border-violet-200 shadow-sm hover:bg-violet-50 transition-all"
                >
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#6b59c6]/10">
                    <FaWallet className="text-[11px] text-[#6b59c6]" />
                  </span>
                  Withdraw
                </motion.button>
              </div>

            </div>

            {enrollBadge}
          </div>
        </motion.div>

        {/* Right side */}
        <motion.div
          variants={fadeUp(0.1)}
          initial="hidden"
          animate="visible"
          className="min-w-0 overflow-hidden rounded-xl border bg-white shadow-sm md:flex md:min-h-0 md:flex-col"
        >
          <div className="shrink-0 border-b bg-gradient-to-b from-slate-50 to-white px-3 py-2.5 sm:px-4 sm:py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <span>Scheme Transactions</span>
                  <span className="text-[11px] text-slate-500 font-semibold">
                    {txnLoading ? "" : `(${filteredTx.length})`}
                  </span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {selectedEnrollment?._id
                    ? "Selected enrollment transactions"
                    : "All scheme transactions"}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <ListLoadingPill show={!!customerId && txnLoading} />

                {customerId ? (
                  <button
                    type="button"
                    onClick={() => setShowReversed((p) => !p)}
                    className="inline-flex items-center rounded-full border px-3 py-1 text-[10px] font-semibold bg-white text-slate-700 hover:bg-slate-50"
                    title="Show/Hide reversed + reversal entries"
                  >
                    {showReversed ? "Hide Reversed" : "Show Reversed"}
                  </button>
                ) : null}
              </div>
            </div>

            <div className="mt-3 flex snap-x snap-mandatory gap-1.5 overflow-x-auto overscroll-x-contain scroll-smooth pb-0.5 text-[10px] [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:grid sm:snap-none sm:grid-cols-5 sm:overflow-visible sm:pb-0">
              <div className="inline-flex h-8 min-w-[48%] snap-start items-center gap-1.5 rounded-lg border border-violet-100 bg-violet-50/70 px-2 text-violet-700 sm:min-w-0">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-violet-500" />
                <span className="shrink-0 font-medium">Target</span>
                <span className="truncate font-bold tabular-nums">
                  {schemeSummary.target}
                </span>
              </div>
              <div className="inline-flex h-8 min-w-[48%] snap-start items-center gap-1.5 rounded-lg border border-emerald-100 bg-emerald-50/70 px-2 text-emerald-700 sm:min-w-0">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                <span className="shrink-0 font-medium">Received</span>
                <span className="truncate font-bold tabular-nums">
                  {schemeSummary.received}
                </span>
              </div>
              <div className="inline-flex h-8 min-w-[48%] snap-start items-center gap-1.5 rounded-lg border border-amber-100 bg-amber-50/70 px-2 text-amber-700 sm:min-w-0">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                <span className="shrink-0 font-medium">Withdrawn</span>
                <span className="truncate font-bold tabular-nums">
                  {schemeSummary.withdrawn}
                </span>
              </div>
              <div className="inline-flex h-8 min-w-[48%] snap-start items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2 text-slate-700 sm:min-w-0">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
                <span className="shrink-0 font-medium">Net</span>
                <span className="truncate font-bold tabular-nums">
                  {schemeSummary.net}
                </span>
              </div>
              <div className="inline-flex h-8 min-w-[48%] snap-start items-center gap-1.5 rounded-lg border border-rose-100 bg-rose-50/70 px-2 text-rose-700 sm:min-w-0">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-rose-500" />
                <span className="shrink-0 font-medium">Pending</span>
                <span className="truncate font-bold tabular-nums">
                  {schemeSummary.pending}
                </span>
              </div>
            </div>
          </div>

          <div className="relative p-3 sm:p-4 md:min-h-0 md:flex-1 md:overflow-hidden">
            <ListShimmerOverlay show={showListSkeleton} />

            {!customerId ? (
              <EmptyState
                icon={<FaUserCircle className="text-[22px]" />}
                title="Select customer"
                subtitle="Customer choose karo, transactions dikh jayenge."
              />
            ) : showListSkeleton ? (
              <div className="space-y-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <SkeletonTxRow key={i} />
                ))}
              </div>
            ) : !filteredTx.length ? (
              <EmptyState
                icon={<FaMoneyBillWave className="text-[22px]" />}
                title="No transactions"
                subtitle={
                  showReversed
                    ? "Is enrollment ke liye koi scheme transaction nahi mila."
                    : "Is enrollment ke liye active (non-reversed) entry nahi mili."
                }
              />
            ) : (
              <div className="max-h-[58dvh] space-y-2 overflow-auto pr-1 md:h-full md:max-h-none">
                {filteredTx.map((r, idx) => {
                  const txnType = pickSchemeTxnType(r); // SCHEME / SCHEME_PAYOUT
                  const isPayout = txnType === "SCHEME_PAYOUT";

                  const reversalRow = isReversalRow(r);
                  const reversedOriginal = !!r?.isReversed;

                  // schemeType could be empty on reversal row; take from row or enrollment
                  const st = String(
                    r?.schemeType || selectedEnrollment?.schemeType || "",
                  ).toUpperCase();
                  const isGram = st === "GRAM";

                  const grams = num(r?.schemeGrams);
                  const amtCredit = num(r?.credit);
                  const amtDebit = num(r?.debit);

                  const primaryValue = isGram
                    ? grams > 0
                      ? fmtG(grams)
                      : "—"
                    : fmtINR(isPayout ? amtDebit : amtCredit);

                  const secondaryValue =
                    isGram && (amtCredit > 0 || amtDebit > 0)
                      ? fmtINR(isPayout ? amtDebit : amtCredit)
                      : null;

                  const title =
                    r?.itemsSummary ||
                    (reversalRow
                      ? r?.note || "Reversal entry"
                      : isPayout
                        ? "Scheme payout"
                        : "Scheme installment");

                  const ledgerIdForPrint = pickTxLedgerId(r);
                  const canPrint = !!(ledgerIdForPrint || r?.printUrl);

                  const badgeText = reversalRow
                    ? "REVERSAL"
                    : reversedOriginal
                      ? "REVERSED"
                      : isPayout
                        ? "PAYOUT"
                        : "RECEIVED";

                  const badgeClass = reversalRow
                    ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                    : reversedOriginal
                      ? "bg-slate-100 text-slate-600 border-slate-200"
                      : isPayout
                        ? "bg-amber-50 text-amber-700 border-amber-200"
                        : "bg-emerald-50 text-emerald-700 border-emerald-200";

                  return (
                    <motion.div
                      key={r?._id || `${idx}`}
                      custom={Math.min(idx, 10)}
                      variants={listVariants}
                      initial="hidden"
                      animate="visible"
                      className={`rounded-2xl border bg-white px-3 py-2.5 shadow-sm hover:shadow-md transition-all ${
                        reversalRow || reversedOriginal ? "opacity-80" : ""
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <div
                              className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-extrabold border ${badgeClass}`}
                            >
                              {badgeText}
                            </div>

                            <div className="text-[12px] font-extrabold text-slate-800 truncate">
                              {title}
                            </div>
                          </div>

                          <div className="text-[10px] text-slate-500 mt-1">
                            {toYmd(r?.date)} ·{" "}
                            {r?.paymentMode || r?.mode || "-"}
                            {r?.txnNo ? ` · ${r.txnNo}` : ""}
                            {r?.enrollmentCode
                              ? ` · ${r.enrollmentCode}`
                              : selectedEnrollment?.enrollmentCode
                                ? ` · ${selectedEnrollment.enrollmentCode}`
                                : ""}
                            {reversalRow ? " · (reversal)" : ""}
                            {reversedOriginal ? " · (reversed)" : ""}
                          </div>

                          {r?.note ? (
                            <div className="text-[10px] text-slate-500 mt-1 line-clamp-2">
                              {r.note}
                            </div>
                          ) : null}

                          {isGram ? (
                            <div className="mt-1 text-[10px] text-slate-500">
                              Metal:{" "}
                              <span className="font-semibold text-slate-700">
                                {r?.metalType ||
                                  selectedEnrollment?.metalType ||
                                  "GOLD"}
                              </span>
                              {num(r?.schemeRatePerGram) > 0 ? (
                                <>
                                  {" · "}Rate:{" "}
                                  <span className="font-semibold text-slate-700">
                                    {fmtINR(r.schemeRatePerGram)}
                                  </span>
                                  <span className="text-slate-500"> /g</span>
                                </>
                              ) : null}
                            </div>
                          ) : null}

                          {reversalRow && r?.reversalOf ? (
                            <div className="mt-1 text-[10px] text-slate-500">
                              Reversal Of:{" "}
                              <span className="font-semibold text-slate-700">
                                {String(r.reversalOf)}
                              </span>
                            </div>
                          ) : null}
                        </div>

                        <div className="text-right shrink-0">
                          <div
                            className={`text-[15px] sm:text-base font-extrabold ${
                              reversalRow || reversedOriginal
                                ? "text-slate-400"
                                : "text-slate-900"
                            }`}
                          >
                            {primaryValue}
                          </div>

                          {secondaryValue ? (
                            <div className="text-[10px] text-slate-500">
                              {secondaryValue}
                            </div>
                          ) : (
                            <div className="text-[10px] text-slate-500">
                              {isPayout ? "Debit" : "Credit"}
                            </div>
                          )}

                          <div className="mt-1.5 flex items-center justify-end gap-2">
                            <button
                              type="button"
                              disabled={!canPrint}
                              onClick={() =>
                                handleReceiptPrint(ledgerIdForPrint)
                              }
                              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold ${
                                canPrint
                                  ? "border-violet-200 bg-white text-[#6b59c6] hover:bg-violet-50"
                                  : "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                              }`}
                              title={
                                canPrint ? "Print slip" : "Slip not available"
                              }
                            >
                              <FiPrinter className="text-[12px]" />
                              Slip
                            </button>

                            <button
                              type="button"
                              disabled={
                                !isReversibleRow(r) || reverseState.isLoading
                              }
                              onClick={() =>
                                openReverse(r, title, primaryValue)
                              }
                              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold ${
                                !isReversibleRow(r) || reverseState.isLoading
                                  ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                                  : "border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
                              }`}
                              title={
                                reversalRow
                                  ? "Reversal row cannot be reversed"
                                  : reversedOriginal
                                    ? "Already reversed"
                                    : "Reverse transaction"
                              }
                            >
                              <FiRotateCcw className="text-[12px]" />
                              Reverse
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      <SchemePayWithdrawModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        customerId={customerId}
        branchId={
          selectedEnrollment?.branchId || selectedEnrollment?.branch?._id
        }
        enrollment={selectedEnrollment}
        action={modalAction}
        onSuccess={() => {
          refetchTxns();
          refetchEnrollments();
        }}
        onPrintReceipt={(ledgerId) => {
          handleReceiptPrint(ledgerId);
        }}
      />

      {/* Reverse confirm modal */}
      <AnimatePresence>
        {reverseOpen && (
          <motion.div
            className="fixed inset-0 z-[10010] bg-black/45 backdrop-blur-sm flex items-center justify-center p-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget && !reverseState.isLoading) {
                setReverseOpen(false);
                setReverseTarget(null);
              }
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              className="w-full max-w-md rounded-3xl bg-white shadow-2xl border border-slate-200 overflow-hidden"
            >
              <div className="px-4 py-3 border-b bg-gradient-to-r from-rose-600 via-rose-500 to-amber-500 text-white">
                <div className="text-[13px] font-extrabold">
                  Reverse transaction?
                </div>
                <div className="text-[11px] text-white/90 mt-0.5">
                  This will create a reverse entry and update balances.
                </div>
              </div>

              <div className="p-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 text-[11px]">
                  <div className="text-slate-700">
                    <span className="font-semibold">Title:</span>{" "}
                    {reverseTarget?.title || "-"}
                  </div>
                  <div className="text-slate-700 mt-1">
                    <span className="font-semibold">Amount:</span>{" "}
                    {reverseTarget?.amountLabel || "-"}
                  </div>
                  <div className="text-slate-500 mt-1">
                    LedgerId: {reverseTarget?.ledgerId || "-"}
                  </div>
                </div>

                <div className="mt-3 flex flex-col sm:flex-row gap-2 justify-end">
                  <button
                    type="button"
                    disabled={reverseState.isLoading}
                    onClick={() => {
                      setReverseOpen(false);
                      setReverseTarget(null);
                    }}
                    className="w-full sm:w-auto inline-flex items-center justify-center rounded-2xl sm:rounded-full border border-slate-300 bg-slate-50 px-4 py-2 text-[11px] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    disabled={reverseState.isLoading}
                    onClick={confirmReverse}
                    className="w-full sm:w-auto inline-flex items-center justify-center rounded-2xl sm:rounded-full bg-rose-600 px-4 py-2 text-[11px] font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
                  >
                    {reverseState.isLoading ? "Reversing..." : "Yes, Reverse"}
                  </button>
                </div>

                <div className="mt-2 text-[10px] text-slate-500">
                  Tip: Slip print ke liye “Slip” use karo. Reverse ke baad entry
                  list me reflect hogi.
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <InvoicePrintPreviewModal
        open={receiptPreview.open}
        html={receiptPreview.html}
        loading={receiptPreview.loading}
        onClose={closeReceiptPreview}
      />
    </div>
  );
}

function EmptyState({ icon, title, subtitle }) {
  return (
    <div className="rounded-2xl border bg-slate-50 p-4 text-center">
      <div className="mx-auto mb-2 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white border shadow-sm text-slate-700">
        {icon}
      </div>
      <div className="text-sm font-extrabold text-slate-800">{title}</div>
      <div className="text-[11px] text-slate-500 mt-1">{subtitle}</div>
    </div>
  );
}
